import type { ComponentProps, ReactNode } from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@shared/lib';

export const avatarVariants = cva(
  'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-surface text-fg',
  {
    variants: {
      size: {
        xs: 'size-6 text-2xs',
        sm: 'size-8 text-xs',
        md: 'size-10 text-sm',
        lg: 'size-14 text-base',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

/** Derive up to two uppercase initials from a display name. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase();
}

export interface AvatarProps
  extends ComponentProps<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  /** Image URL. Falls back to initials/`fallback` while loading or on error. */
  src?: string | undefined;
  /** Display name — used as the image `alt` and to derive initials. */
  name?: string;
  /** Override the fallback content (e.g. a group-type icon). */
  fallback?: ReactNode;
}

/**
 * User/group avatar built on Radix. Shows the image when it loads, otherwise initials
 * derived from `name` (or a custom `fallback`). **A11y:** the image carries `alt={name}`;
 * a name-less, image-less avatar is decorative.
 */
export function Avatar({ className, size, src, name, fallback, ...props }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ size }), className)}
      {...props}
    >
      {src ? (
        <AvatarPrimitive.Image src={src} alt={name ?? ''} className="size-full object-cover" />
      ) : null}
      <AvatarPrimitive.Fallback
        delayMs={src ? 200 : 0}
        className="flex size-full items-center justify-center font-medium text-muted"
      >
        {fallback ?? initials(name ?? '')}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}
